import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';

const HERO_IMAGES = [
  {
    url: "https://images.unsplash.com/photo-1504674900247-0877df9cc836?q=80&w=2000&auto=format&fit=crop",
    alt: "Authentic Mexican Tacos Platter"
  },
  {
    url: "https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?q=80&w=2000&auto=format&fit=crop",
    alt: "Fresh Crafted Cocktails"
  },
  {
    url: "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?q=80&w=2000&auto=format&fit=crop",
    alt: "Restaurant Interior Atmosphere"
  },
  {
    url: "https://images.unsplash.com/photo-1565299585323-38d6b0865b47?q=80&w=2000&auto=format&fit=crop",
    alt: "Traditional Sizzling Fajitas"
  }
];

const FEATURED_DISHES = [
  {
    name: "Tacos Al Pastor",
    desc: "Slow-roasted marinated pork, fresh pineapple, cilantro, and onions on handmade corn tortillas.",
    img: "https://images.unsplash.com/photo-1551504734-5ee1c4a1479b?q=80&w=800&auto=format&fit=crop"
  },
  {
    name: "Ceviche Tradicional",
    desc: "Fresh citrus-cured catch of the day with red onion, jalapeño, and creamy avocado slices.",
    img: "https://images.unsplash.com/photo-1599974579688-8dbdd335c77f?q=80&w=800&auto=format&fit=crop"
  },
  {
    name: "Tacos de Camarón",
    desc: "Crispy Pacific shrimp, spicy chipotle slaw, and fresh mango salsa in a warm tortilla.",
    img: "https://images.unsplash.com/photo-1593560708920-61dd98c46a4e?q=80&w=800&auto=format&fit=crop"
  }
];

const Home = () => {
  const [currentImage, setCurrentImage] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % HERO_IMAGES.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="flex flex-col w-full h-full">
      {/* Hero Section */}
      <section className="flex-grow flex flex-col lg:flex-row relative px-8 md:px-16 pt-10 pb-20">
        <div className="w-full lg:w-1/2 flex flex-col justify-center z-10 pr-0 lg:pr-12 pt-10 lg:pt-0">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }}>
            <h1 className="text-7xl md:text-8xl lg:text-[130px] leading-[0.85] text-olive mb-6">
              SABOR QUE<br />TE ENAMORA
            </h1>
            <div className="flex items-center gap-4 text-rust mb-8 w-full max-w-sm">
              <div className="flex-grow border-t border-rust relative">
                 <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-2 h-2 border-t-2 border-r-2 border-rust rotate-45"></div>
              </div>
              <SafeIcon name="Sun" className="w-6 h-6" />
              <div className="flex-grow border-t border-rust relative">
                 <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-2 h-2 border-t-2 border-l-2 border-rust -rotate-45"></div>
              </div>
            </div>
            <p className="text-lg md:text-xl text-olive mb-10 max-w-md font-medium leading-relaxed">
              Authentic Mexican flavors made with fresh ingredients and traditional recipes.<br/>
              <span className="block mt-2 font-display text-2xl tracking-wide">Bienvenido a nuestra casa.</span>
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/menu" className="btn-primary">VIEW MENU</Link>
              <button className="btn-outline">MAKE A RESERVATION</button>
            </div>
          </motion.div>
        </div>

        <div className="w-full lg:w-1/2 h-[50vh] lg:h-auto mt-12 lg:mt-0 relative rounded-tl-[100px] overflow-hidden shadow-2xl bg-olive/5">
          <AnimatePresence mode="wait">
            <motion.img
              key={currentImage}
              src={HERO_IMAGES[currentImage].url}
              alt={HERO_IMAGES[currentImage].alt}
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 1.5, ease: "easeInOut" }}
              className="absolute inset-0 w-full h-full object-cover object-center"
            />
          </AnimatePresence>
          <div className="absolute inset-0 bg-gradient-to-r from-sand via-transparent to-transparent opacity-40 hidden lg:block pointer-events-none"></div>
          <div className="absolute bottom-6 right-8 flex gap-2">
            {HERO_IMAGES.map((_, idx) => (
              <div key={idx} className={`h-1 transition-all duration-500 rounded-full ${idx === currentImage ? 'w-8 bg-rust' : 'w-2 bg-white/50'}`} />
            ))}
          </div>
        </div>
      </section>

      {/* Featured Dishes Section */}
      <section className="py-24 px-8 md:px-16 bg-sand-light border-y border-olive/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-rust font-semibold tracking-widest uppercase text-sm mb-2 block">Our Favorites</span>
            <h2 className="text-5xl md:text-6xl text-olive mb-4">PLATOS DESTACADOS</h2>
            <p className="text-olive/80 max-w-xl mx-auto text-lg">A taste of our most beloved creations, crafted with passion and authentic ingredients.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {FEATURED_DISHES.map((dish, idx) => (
              <motion.div 
                key={idx} 
                whileHover={{ y: -10 }}
                className="group cursor-pointer bg-sand p-4 rounded-tr-[60px] rounded-bl-[60px] shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <div className="overflow-hidden rounded-tr-[50px] rounded-bl-[50px] mb-6 h-72 shadow-inner">
                  <img src={dish.img} alt={dish.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                </div>
                <h3 className="text-3xl font-display text-rust mb-3 uppercase tracking-wide px-2">{dish.name}</h3>
                <p className="text-olive/80 text-base leading-relaxed px-2 mb-4">{dish.desc}</p>
                <div className="px-2 pb-2">
                  <Link to="/menu" className="text-olive font-bold text-sm underline decoration-rust underline-offset-4 hover:text-rust transition-colors">ORDER NOW</Link>
                </div>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-16">
            <Link to="/menu" className="btn-outline inline-block px-12">EXPLORE FULL MENU</Link>
          </div>
        </div>
      </section>

      {/* Features Bar */}
      <section className="bg-olive-dark text-sand py-20 px-8 md:px-16 mt-auto">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 text-center divide-y md:divide-y-0 md:divide-x divide-sand/10">
          <div className="flex flex-col items-center pt-8 md:pt-0">
            <div className="text-rust mb-6 h-12 flex items-center justify-center"><SafeIcon name="Feather" className="w-10 h-10" /></div>
            <h3 className="text-xl tracking-wider mb-3 uppercase font-display">AUTHENTIC RECIPES</h3>
            <p className="text-sm opacity-80 max-w-[200px]">Traditional dishes made exactly the way our abuelas taught us.</p>
          </div>
          <div className="flex flex-col items-center pt-8 md:pt-0 md:pl-4">
            <div className="text-rust mb-6 h-12 flex items-center justify-center"><SafeIcon name="Sun" className="w-10 h-10" /></div>
            <h3 className="text-xl tracking-wider mb-3 uppercase font-display">FRESH INGREDIENTS</h3>
            <p className="text-sm opacity-80 max-w-[200px]">Locally sourced produce and organic corn for our tortillas.</p>
          </div>
          <div className="flex flex-col items-center pt-8 md:pt-0 md:pl-4">
            <div className="text-rust mb-6 h-12 flex items-center justify-center"><SafeIcon name="Coffee" className="w-10 h-10" /></div>
            <h3 className="text-xl tracking-wider mb-3 uppercase font-display">CRAFTED DRINKS</h3>
            <p className="text-sm opacity-80 max-w-[200px]">Handmade margaritas and artisanal tequila selections.</p>
          </div>
          <div className="flex flex-col items-center pt-8 md:pt-0 md:pl-4">
            <div className="text-rust mb-6 h-12 flex items-center justify-center"><SafeIcon name="Smile" className="w-10 h-10" /></div>
            <h3 className="text-xl tracking-wider mb-3 uppercase font-display">WARM HOSPITALITY</h3>
            <p className="text-sm opacity-80 max-w-[200px]">Every guest is familia. Experience the heart of Mexico.</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;